import { computed } from "vue"
import axios from "axios"
import { useQuery } from "@tanstack/vue-query"
import { toast } from "@/toast"
import { queryClient } from "@/query.js"
import { cachedAuthorizationUser } from "@/composables/useAccount.js"

const currentUser = cachedAuthorizationUser()

const ruleSet = {
  manageSubscription: {
    role: /^(owner|direct_reseller|distributor)$/,
    type: "subscription",
  },
  manageInstance: {
    role: /^(owner|admin|direct_reseller|involved_reseller)$/,
    type: "subscription",
  },
  manageInvoices: {
    role: /^(owner|direct_reseller|distributor)$/,
    type: "subscription",
  },
  uploadFiles: {
    role: /^(owner|direct_reseller|involved_reseller)$/,
    type: "subscription",
  },
}

const fetchAllPermissionsQuery = {
  queryKey: ["fetchAllPermissions"],
  async queryFn() {
    try {
      const { data } = await axios.get("/authorization/permissions")
      return data
    }
    catch (e) {
      toast.error(e)
      return {}
    }
  },
  placeholderData: () => ({}),
}

const fetchSelfServicePermissionQuery = {
  queryKey: ["fetchSelfServicePermission"],
  async queryFn() {
    try {
      const partnerRoles = ["partner", "distributor", "direct_reseller", "involved_reseller"]
      const { role, sub } = await currentUser.fetchAuthorizationUser()
      if (!partnerRoles.includes(role)) {
        return false
      }
      const { data } = await axios.get("/partner/subscriptions/can_get_monthly_subscription", {
        params: { customer_id: sub },
      })
      return !!data.canGetMonthlySubscription
    }
    catch (e) {
      toast.error(e)
      return false
    }
  },
  placeholderData: () => false,
}

const fetchAllPermissions = () => queryClient.fetchQuery(fetchAllPermissionsQuery)
const fetchSelfServicePermission = () => queryClient.fetchQuery(fetchSelfServicePermissionQuery)

const hasPermission = (permissions, permission, resourceId) => {
  const resource = permissions.resources?.find(x => x.resourceId === resourceId)

  if (!resource) {
    return false
  }

  const rule = ruleSet[permission] || {
    role: /ˆNO_PERMISSION$/,
    type: "NO_PERMISSION",
  }

  return resourceId === resource.resourceId
    && rule.role.test(resource.role)
    && rule.type === resource.resourceType
}

const usePermissions = (permission, resourceId) => {
  const { data: permissions, isFetching: isFetching1 }
    = useQuery(fetchAllPermissionsQuery)
  const { data: isSelfServiceAllowed, isFetching: isFetching2 }
    = useQuery(fetchSelfServicePermissionQuery)
  return {
    isFetching: computed(() => isFetching1.value || isFetching2.value),
    isSelfServiceAllowed,
    hasPermission: computed(() => hasPermission(permissions.value, permission.value, resourceId.value)),
  }
}

export {
  usePermissions,
  fetchAllPermissions,
  fetchSelfServicePermission,
  hasPermission,
}
